import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Управление привязкой локального договора к 1С.
 *
 * Три режима, в зависимости от текущего состояния:
 *  • Договор уже привязан (есть onec.agreementId)        → показываем код, кнопка «Отвязать»
 *  • Договор не привязан, у ученика есть контрагент       → 2 опции:
 *      «Связать с существующим в 1С» (пикер из кэша)
 *      «Создать новый в 1С»            (POST onec-push)
 *  • Договор не привязан, контрагента у ученика нет       → блок с предупреждением
 *
 * onChanged(agreement) вызывается после успешного действия с обновлённой моделью договора.
 */
export default function AgreementOnecLinkModal({ student, agreement, onClose, onChanged }) {
  const [working, setWorking] = useState(false);
  const [error, setError]     = useState(null);

  const [cacheRows, setCacheRows] = useState(null); // существующие onec_agreements контрагента
  const [search, setSearch]       = useState('');

  const onec = agreement.onec || {};
  const linked = !!onec.agreementId;

  const contractor = student.onecContractor;
  const canLinkOrPush = !!contractor;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Загрузка списка onec_agreements этого контрагента
  useEffect(() => {
    if (!canLinkOrPush || linked) return;
    let aborted = false;
    api.get(`/api/onec/agreements?contractor_id=${contractor.id}`)
      .then((res) => { if (!aborted) setCacheRows(res.rows || []); })
      .catch(() => { if (!aborted) setCacheRows([]); });
    return () => { aborted = true; };
  }, [canLinkOrPush, linked, contractor?.id]);

  const filteredCache = useMemo(() => {
    if (!cacheRows) return [];
    const s = search.trim().toLowerCase();
    if (!s) return cacheRows;
    return cacheRows.filter((r) =>
      (r.code || '').toLowerCase().includes(s) ||
      (r.number || '').toLowerCase().includes(s) ||
      (r.name || '').toLowerCase().includes(s)
    );
  }, [cacheRows, search]);

  const onecPush = async () => {
    setWorking(true); setError(null);
    try {
      const res = await api.post(`/api/agreements/${agreement.id}/onec-push`, {});
      onChanged?.(res.agreement);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось создать в 1С');
    } finally { setWorking(false); }
  };

  const onecLink = async (onecAgreementId) => {
    setWorking(true); setError(null);
    try {
      const res = await api.post(`/api/agreements/${agreement.id}/onec-link`, { onecAgreementId });
      onChanged?.(res.agreement);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось привязать');
    } finally { setWorking(false); }
  };

  const onecUnlink = async () => {
    if (!confirm('Отвязать договор от 1С? Запись в кэше 1С останется.')) return;
    setWorking(true); setError(null);
    try {
      const res = await api.delete(`/api/agreements/${agreement.id}/onec-link`);
      onChanged?.(res.agreement);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось отвязать');
    } finally { setWorking(false); }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>Связь с 1С</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <div className="sd-edit__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Текущий статус */}
          <div style={{
            padding: 12, borderRadius: 8,
            background: linked ? 'var(--selected)' : 'var(--panel-2)',
            border: '1px solid ' + (linked ? 'var(--selected-border)' : 'var(--border)'),
          }}>
            <div style={{ fontSize: 11, color: 'var(--fg-faint)', textTransform: 'uppercase',
                          letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>
              Текущее состояние
            </div>
            {linked ? (
              <>
                <div style={{ fontSize: 13, color: 'var(--fg)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {onec.code
                    ? <span className="mono" style={{ fontWeight: 500 }}>{onec.code}</span>
                    : <span style={{ color: 'var(--amber-fg)' }}>ожидает код 1С</span>}
                  {onec.syncState === 'pending_create' && (
                    <span className="agr-row__pending" title={onec.syncError || ''}>в очереди</span>
                  )}
                  {onec.syncState === 'error' && (
                    <span className="agr-row__error" title={onec.syncError || ''}>ошибка</span>
                  )}
                </div>
                {onec.syncError && (
                  <div style={{ fontSize: 12, color: 'var(--red-fg)', marginTop: 4 }}>{onec.syncError}</div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>Договор не привязан к 1С</div>
            )}
          </div>

          {!linked && !canLinkOrPush && (
            <div className="sd-edit__hint" style={{ color: 'var(--red-fg)' }}>
              У ученика не привязан контрагент 1С. Сначала привяжите контрагента в карточке ученика —
              без этого нельзя ни создать договор в 1С, ни найти существующий для привязки.
            </div>
          )}

          {!linked && canLinkOrPush && (
            <>
              <div>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={onecPush}
                  disabled={working}
                  style={{ width: '100%', justifyContent: 'center', height: 36 }}
                >
                  <Icon name="plus" size={13} />
                  Создать новый договор в 1С
                </button>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 6 }}>
                  Будет отправлен в 1С с номером «{agreement.displayNumber || agreement.number}» от {agreement.date}.
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginBottom: 8 }}>
                  …или связать с существующим в 1С:
                </div>
                <input
                  className="sd-edit__input"
                  placeholder="Поиск по коду или номеру…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div style={{
                  marginTop: 8, maxHeight: 220, overflowY: 'auto',
                  border: '1px solid var(--border)', borderRadius: 7,
                }}>
                  {cacheRows === null && (
                    <div style={{ padding: 12, color: 'var(--fg-muted)', fontSize: 12 }}>Загрузка…</div>
                  )}
                  {cacheRows && filteredCache.length === 0 && (
                    <div style={{ padding: 12, color: 'var(--fg-faint)', fontSize: 12 }}>
                      {cacheRows.length === 0 ? 'У контрагента пока нет договоров в 1С' : 'Ничего не найдено'}
                    </div>
                  )}
                  {filteredCache.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => onecLink(r.id)}
                      disabled={working}
                      style={{
                        width: '100%', textAlign: 'left',
                        padding: '8px 12px', border: 0, background: 'transparent',
                        borderBottom: '1px solid var(--border)', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10,
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <span className="mono" style={{ fontSize: 12, color: 'var(--fg-muted)', minWidth: 100 }}>
                        {r.code || '— нет кода —'}
                      </span>
                      <span style={{ flex: 1, fontSize: 13 }}>
                        № {r.number}
                        {r.date && <span style={{ color: 'var(--fg-faint)' }}> · {r.date.split('-').reverse().join('.')}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {linked && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                onClick={onecUnlink}
                disabled={working}
                style={{ height: 32 }}
              >
                <Icon name="x" size={12} /> Отвязать от 1С
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 6 }}>
                Запись в кэше 1С останется. Локальный договор будет помечен как «без 1С».
              </div>
            </div>
          )}
        </div>

        <div className="sd-edit__foot">
          {error && <div className="sd-edit__error">{error}</div>}
          <div className="sd-edit__foot-spacer" />
          <button type="button" className="btn btn--sm" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
